/*
Caution: this must NOT unconditionally import large unnecessary dependencies,
such as views, neither directly nor indirectly. This is the browser entry
point, and must be kept as small as possible. Large dependencies must be
conditional / lazy / on-demand / route-based.
*/

/* global document, customElements */

import * as a from '@mitranim/js/all.mjs'
import * as v from '#iso/vars.mjs'
import * as c from './conf.mjs'
import * as u from './util.mjs'
import * as ce from './client-env.mjs'

/*
This import tells the bundler that the common "elem" modules are needed by the
app entry point, changing how it "chunks" them with other modules. Otherwise,
some common element modules would be chunked with some huge "view" modules
which are not actually needed on startup, and unnecessary code would delay
initialization.
*/
import './elem.mjs'

async function main() {
  const env = new ce.ClientEnv({baseUrl: globalThis.location.origin})

  // Enables `getEnv` across all code.
  v.ENV.set(env)

  // Makes debugging more convenient.
  a.final(globalThis, `env`, env)

  // Fallback. We also do this in SSR, and ideally this line should do nothing.
  u.initDeviceId(env)

  env.init()

  const scripts = getDelayedScripts()

  try {
    await Promise.allSettled(a.map(globalThis.process?.env?.scripts, toImport))
    u.REG.setDefiner(customElements)

    /*
    Should be done after registering custom elements, otherwise `logAlert` will
    not work, as it needs `Notif`.

    Should be done before routing, because this may perform a quiet pushstate
    redirect to remove some query keys. The method is async, but the redirect
    is synchronous, at least at the time of writing.
    */
    env.ana.detectAuth().catch(u.logAlert)

    // Used in development when `c.DEV_SKIP_PRERENDER`.
    if (ce.ensureRoot().hasAttribute(c.ATTR_BODY_EMPTY)) {
      await env.transition(env.ctx)
    }

    await env.initDelay.vac()
  }
  finally {
    env.initDelay = undefined
    ready(env)
  }

  initDelayedScripts(scripts)
  if (c.FCM_ENABLED) u.authFcm(env.bg).catch(u.logAlertOpt)
}

function toImport(src) {
  // Caution: must be on its own line to prevent esbuild from attempting to
  // bundle ALL the files including ones we don't actually import.
  src = `/` + a.reqValidStr(src)
  return u.imp(import(src))
}

function getDelayedScripts() {
  return u.nodesByAttr(document, `type`, c.SCRIPT_TYPE_DELAYED)
}

function initDelayedScripts(src) {
  for (const val of src) {
    val.type = a.laxStr(val.dataset.type)
    delete val.dataset.type
    document.body.append(val)
  }
}

/*
Allows tests to detect when the JS app is fully initialized.
This is a workaround for the fact that until the first render,
inputs may be interactive, but their state will be reset
on the first render.

Must be run after the first render, and only once.
*/
function ready() {
  document.documentElement.dataset.ready = `true`
  document.dispatchEvent(new CustomEvent(`ready`))
}

main().catch(u.onUnhandledErr)
